// @ts-strict-ignore
import { createReducer, on } from '@ngrx/store';
import * as CameraActions from '../actions/camera.actions';
import { CameraTimeline } from '../../service/vehicale-dvr-video.serevice';
import { CameraInfo } from '../../models/camera/camera-info';
import { CameraState } from '../../models/camera/camera-state';
import { ClipInterface } from '../../models/camera/clip.interface';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

export const cameraFeatureKey = 'camera';

export interface State extends EntityState<ClipInterface> {
  cameraHistory: CameraTimeline;
  cameraHistoryLoading: boolean;
  sortBy: string;
  filterBy: string[];
  loading: boolean;
  loadingClips: boolean;
  currentCameraInfo: CameraInfo;
  cameraStates: CameraState[];
  clipsCount: number;
  error: any;
}

export const adapter: EntityAdapter<ClipInterface> =
  createEntityAdapter<ClipInterface>();

export const initialState: State = adapter.getInitialState({
  cameraHistory: null,
  cameraHistoryLoading: false,
  sortBy: 'lower',
  filterBy: [],
  loading: false,
  loadingClips: false,
  currentCameraInfo: null,
  cameraStates: [],
  clipsCount: 0,
  error: null,
});

export const reducer = createReducer(
  initialState,
  on(
    CameraActions.getVideoRequestEvent,
    CameraActions.updateVideoClip,
    (state) => ({ ...state, loading: true }),
  ),
  on(CameraActions.loadClips, (state) => ({
    ...state,
    ...adapter.removeAll(state),
    loadingClips: true,
  })),
  on(CameraActions.loadClipsSuccess, (state, action) => {
    return {
      ...adapter.setAll(action.data, state),
      loadingClips: false,
    };
  }),
  on(CameraActions.loadCurrentCameraStatus, (state) => ({
    ...state,
    loading: true,
  })),
  on(
    CameraActions.clearAllCameraInfo,
    (state): State => ({
      ...state,
      loading: false,
      currentCameraInfo: null,
      cameraStates: [],
    }),
  ),
  on(CameraActions.loadCurrentCameraStatusSuccess, (state, action) => {
    let cameraStates = state.cameraStates;
    if (!!action.cameraState)
      cameraStates = [...state.cameraStates, action.cameraState];
    return {
      ...state,
      cameraStates,
      loading: false,
    };
  }),
  on(CameraActions.setCameraStateFromEventSource, (state, action) => {
    const cameraState = action.state;
    const cameraStates: CameraState[] = state.cameraStates;
    const cameraStateIndex = cameraStates.findIndex(
      (cameraState) => cameraState.cameraId === cameraState.cameraId,
    );
    if (cameraStateIndex > -1) {
      cameraStates[cameraStateIndex] = cameraState;
    } else {
      cameraStates.push(cameraState);
    }
    return {
      ...state,
      cameraStates,
    };
  }),
  on(CameraActions.setClipProgressFromEventSource, (state, action) => ({
    ...state,
    ...adapter.updateOne(action.clip, state),
  })),
  on(CameraActions.updateVideoClipSuccess, (state, action) => ({
    ...state,
    ...adapter.updateOne(action.clip, state),
    loading: false,
  })),
  on(CameraActions.getVideoRequestEventSuccess, (state, action) => ({
    ...state,
    ...adapter.addOne(action.data, state),
    loading: false,
  })),
  on(CameraActions.loadCamerasFailure, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
  })),
  on(CameraActions.setCameraSortBy, (state, action) => ({
    ...state,
    sortBy: action.sortBy,
  })),
  on(
    CameraActions.getCameraVideoHistory,
    (state): State => ({
      ...state,
      cameraHistory: null,
      cameraHistoryLoading: true,
    }),
  ),
  on(CameraActions.getCameraVideoHistorySuccess, (state, action) => ({
    ...state,
    cameraHistory: action.videoHistory,
    cameraHistoryLoading: false,
  })),
  on(
    CameraActions.getCameraVideoHistoryError,
    (state): State => ({
      ...state,
      cameraHistory: null,
      cameraHistoryLoading: false,
    }),
  ),
  on(
    CameraActions.getCurrentCameraInfo,
    (state): State => ({
      ...state,
      currentCameraInfo: null,
    }),
  ),
  on(CameraActions.getCurrentCameraInfoSuccess, (state, action) => ({
    ...state,
    currentCameraInfo: action.currentCameraInfo,
  })),
  on(CameraActions.deleteVideoClipSuccess, (state, action) => ({
    ...state,
    ...adapter.removeOne(action.clipId, state),
    clipsCount: state.clipsCount - 1,
  })),
  on(CameraActions.setCameraFilterBy, (state, action) => ({
    ...state,
    filterBy: state.filterBy.includes(action.filterBy)
      ? state.filterBy.filter((i) => i !== action.filterBy)
      : [...state.filterBy, action.filterBy],
  })),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();
export const getSortBy = (state: State) => state.sortBy;
export const getFilterBy = (state: State) => state.filterBy;
export const getCameraHistory = (state: State) => state.cameraHistory;
export const getCameraHistoryLoading = (state: State) =>
  state.cameraHistoryLoading;
export const getDvrLoading = (state: State) => state.loading;
export const getLoadingClips = (state: State) => state.loadingClips;
export const getCurrentCameraInfo = (state: State) => state.currentCameraInfo;
export const getCameraStatuses = (state: State) => state.cameraStates;
export const getCameraClipsCount = (state: State) => state.clipsCount;
export const getCameraError = (state: State) => state.error;
